import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import addConditionOutputTool from './addConditionOutputTool';
import editConditionOutputTool from './editConditionOutputTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import readValuesFromExcelTool from '../../../../tools/general/readValuesFromExcelTool/readValuesFromExcelTool';
import searchSignalTool from './searchSignalTool';
import deleteOutuputsTool from '../general/deleteOutputsTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {ConditionCardScratchpadState} from '../../scratchpad/conditionCardScratchpad.types';
import setOperatorTool from './setOperatorTool';
import setSignalTool from './setSignalTool';
import getOrCreateConditionCard from '../../utils/conditions/getOrCreateConditionCard';
import searchFeedValuesTool from './searchFeedValuesTool';
import {FallbackOutputGuidelines} from '../../promptToCardAgentV2.consts';

const cardSpecificInstructions = `1. Use the ${V2CardAgentToolName.SearchSignal} tool to find the best matching signal for matching the user's request.
1.1. The signal has a property called *"shouldQueryOptionalValues"*. If this is true and you are adding/editing condition outputs values, you MUST use the ${V2CardAgentToolName.SearchFeedValues} tool to find the different values that are relevant for the signal.
1.2. Following the signal type returned by the ${V2CardAgentToolName.SearchSignal} tool, use the ${V2CardAgentToolName.AddConditionOutput} or ${V2CardAgentToolName.UpdateConditionOutput} tool to add or update the condition outputs.
2. When settings the outputs, DO NOT change the existing outputs, unless the condition has been changed or the user has requested to change the outputs values.
3. The user may ask to create outputs from an excel file. In such a case, use the ${V2CardAgentToolName.ReadValuesFromExcel} tool to read the outputs from the excel file.

### Condition card specific instructions
${FallbackOutputGuidelines}. 
- Example use case: "Match 'Premium' for value 'premium', 'Standard' for value 'standard'" (NO need to mention "Other")

### IMPORTANT
- ALWAYS check the card's signal type and use the appropriate tool to add or update the condition outputs.
- DO NOT add fallback outputs of any kind, only set the outputs requested by the user.
- If the user did not ask to change the signal, skip the get and set signal steps.`;

export default function addOrUpdateConditionCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, ConditionCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId: cardId} = preProcessStandaloneAgent(params, store);

    // Create the standalone agent definition
    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateConditionCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.CONDITION]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.CONDITION,
        cardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        deleteOutuputsTool(),
        getFilesTool(),
        getCardDetailsTool(),
        readValuesFromExcelTool(),

        //Condition tools
        searchSignalTool(store),
        setSignalTool(),
        setOperatorTool(),
        addConditionOutputTool(),
        editConditionOutputTool(),
        searchFeedValuesTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card: await getOrCreateConditionCard(cardId, store)};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateConditionCard,
    description:
      'Add or update a Condition card. Selects output based on parameter, operator, and matching values per output. Supports reading values from feed columns or Excel.',
    displayName: 'Working on condition card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
